import { FC, useEffect, useState } from "react";
import { Button, color } from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import Line from "../../assets/LineHorizontal.png";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  setRepTitle,
  setRepFirstName,
  setRepLastName,
  setUserFirstName,
  setUserLastName,
  setRepEmail as setRepEmailRedux,
  setMerchantTitle as setMerchantTitleRedux,
  ShareholderDirector,
  setShareholderDirector,
  setMerchantFirstName as setMerchantFirstNameRedux,
  setMerchantLastName as setMerchantLastNameRedux,
  setAuthChecked as setAuthCheckedRedux,
  setDirectorChecked as setDirectorCheckedRedux,
  setNumOfShareholdersDirectors as setNumOfShareholdersDirectorsRedux,
} from "../../features/merchant/rep";
import { useStyles } from "./BorrowerRep.styles";
import { toast } from "react-toastify";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import {
  CompanyRole,
  ShareholderDirectorField,
} from "@funded-here-interface/common/src/constant/enum";
import ShareholdersDirectors from "../../components/ShareholdersDirectors/ShareholdersDirectors";
import User from "./Components/User/User";
import Auth from "../../components/Auth/Auth";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";

// Custom hooks and utilities
import { useBorrowerRepDraftSync } from "./hooks/useBorrowerRepDraftSync";
import {
  determineRepresentativeInfo,
  getDirectorEmails,
  areAllDirectorEmailsPresent,
  wasAuthCheckboxChecked,
  wasDirectorCheckboxChecked,
} from "./utils/authDirectorHelpers";

interface BorrowerRepProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveDraft?: (callback: () => void) => void;
  onResetForm?: (callback: () => void) => void;
  onSubmit?: () => void;
  errors?: { [key: string]: string };
  onClearError?: (field: string) => void;
}

const BorrowerRep: FC<BorrowerRepProps> = ({
  onDirtyChange,
  onSaveDraft,
  onResetForm,
  onSubmit,
  errors = {},
  onClearError,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, userEmail, userId } = useSelector(
    (state: RootState) => state.auth
  );

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const { syncBorrowerRepToRedux } = useBorrowerRepDraftSync();

  // Page and dirty state
  const [initPage, setInitPage] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialAuthChecked, setInitialAuthChecked] = useState(false);
  const [initialDirectorChecked, setInitialDirectorChecked] = useState(false);
  const [initialDraftData, setInitialDraftData] = useState<any>(null);

  // Redux selectors
  const {
    repFirstName: repFirstNameRedux,
    repLastName: repLastNameRedux,
    userFirstName: userFirstNameRedux,
    userLastName: userLastNameRedux,
    repTitle: repTitleRedux,
    merchantTitle: merchantTitleRedux,
    shareholdersDirectors: shareholdersDirectorsRedux,
    repEmail: repEmailRedux,
  } = useSelector((state: RootState) => state.Borrep);

  // Local state
  const [authChecked, setAuthChecked] = useState(false);
  const [directorChecked, setDirectorChecked] = useState(false);
  const [representativeFirstName, setRepresentativeFirstName] = useState(
    repFirstNameRedux || ""
  );
  const [representativeLastName, setRepresentativeLastName] = useState(
    repLastNameRedux || ""
  );
  const [representativeTitle, setRepresentativeTitle] = useState(
    repTitleRedux || ""
  );
  const [merchantTitle, setMerchantTitle] = useState(merchantTitleRedux || "");
  const [merchantFirstName, setMerchantFirstName] = useState(
    userFirstNameRedux || ""
  );
  const [repEmail, setRepEmail] = useState(repEmailRedux || "");
  const [merchantLastName, setMerchantLastName] = useState(
    userLastNameRedux || ""
  );
  const [numOfShareholdersDirectors, setNumOfShareholdersDirectors] = useState(
    shareholdersDirectorsRedux?.length || 1
  );
  const [shareholdersDirectors, setShareholdersDirectors] = useState<
    ShareholderDirector[]
  >(shareholdersDirectorsRedux || []);

  /**
   * Generic input change handler with dirty tracking and error clearing
   */
  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<any>>,
    value: any,
    errorField?: string
  ) => {
    setter((prevValue: any) => {
      if (prevValue !== value && shouldMarkDirty && isDirty === false) {
        setIsDirty(true);
        onDirtyChange?.(true);
      }
      return value;
    });
    if (errorField) {
      onClearError?.(errorField);
    }
  };

  /**
   * Update shareholder/director value with sync to representative fields if needed
   */
  const updateShareholderDirectorValue = (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => {
    // If updating the director who is the representative, sync fields
    if (shareholdersDirectors[index]?.email === repEmail && directorChecked) {
      if (field === ShareholderDirectorField.EMAIL)
        handleInputChange(setRepEmail, value);
      if (field === ShareholderDirectorField.FIRSTNAME) {
        handleInputChange(setRepresentativeFirstName, value);
      }
      if (field === ShareholderDirectorField.LASTNAME) {
        handleInputChange(setRepresentativeLastName, value);
      }
    }

    setShareholdersDirectors((prev) => {
      const updatedShareholdersDirectors: ShareholderDirector[] = [...prev];
      updatedShareholdersDirectors[index] = {
        ...updatedShareholdersDirectors[index],
        [field]: value,
        [ShareholderDirectorField.ROLE]: CompanyRole.DIRECTOR,
      };
      if (shouldMarkDirty) {
        setIsDirty(true);
        onDirtyChange?.(true);
      }
      return updatedShareholdersDirectors;
    });
  };

  const directorEmails = getDirectorEmails(shareholdersDirectors);
  const allDirectorsEmailPresent = areAllDirectorEmailsPresent(
    shareholdersDirectors
  );

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data) {
            setInitialDraftData(data);

            const merchantFirstName = data.userFirstName || "";
            const merchantLastName = data.userLastName || "";
            const merchantTitle = data.merchantTitle || "";
            const shareholdersDirectors = data.shareHoldersDirectors || [];
            const numOfShareholdersDirectors =
              shareholdersDirectors.length || 1;

            // Determine representative info based on checkboxes
            const repInfo = determineRepresentativeInfo({
              authChecked: data.authChecked || false,
              directorChecked: data.directorChecked || false,
              merchantFirstName,
              merchantLastName,
              merchantTitle,
              userEmail: userEmail as string,
              repEmail: data.repEmail || "",
              shareholdersDirectors,
              initialRepFirstName: data.repFirstName || "",
              initialRepLastName: data.repLastName || "",
              initialRepTitle: data.repTitle || "",
            });

            // Set initial checkbox states
            setInitialAuthChecked(repInfo.authChecked);
            setInitialDirectorChecked(repInfo.directorChecked);
            setAuthChecked(repInfo.authChecked);
            setDirectorChecked(repInfo.directorChecked);

            // Set local states
            setRepresentativeFirstName(repInfo.repFirstName);
            setRepresentativeLastName(repInfo.repLastName);
            setRepresentativeTitle(repInfo.repTitle);
            setRepEmail(repInfo.repEmail);
            setMerchantFirstName(merchantFirstName);
            setMerchantLastName(merchantLastName);
            setMerchantTitle(merchantTitle);
            setShareholdersDirectors(shareholdersDirectors);
            setNumOfShareholdersDirectors(numOfShareholdersDirectors);

            // Sync to Redux
            syncBorrowerRepToRedux({
              repFirstName: repInfo.repFirstName,
              repLastName: repInfo.repLastName,
              userFirstName: merchantFirstName,
              userLastName: merchantLastName,
              repEmail: repInfo.repEmail,
              repTitle: repInfo.repTitle,
              merchantTitle,
              merchantFirstName,
              merchantLastName,
              authChecked: repInfo.authChecked,
              directorChecked: repInfo.directorChecked,
              shareHoldersDirectors: shareholdersDirectors,
              numOfShareHoldersDirectors: numOfShareholdersDirectors,
            });

            setIsDirty(false);
            onDirtyChange?.(false);
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };
    initializeDraft();
  }, [initPage, userId, dispatch]);

  /**
   * Handle auth/director checkbox changes
   */
  useEffect(() => {
    if (!shouldMarkDirty) return;

    dispatch(setNumOfShareholdersDirectorsRedux(numOfShareholdersDirectors));

    if (authChecked !== initialAuthChecked && authChecked) {
      handleInputChange(setRepresentativeLastName, merchantLastName);
      handleInputChange(setRepresentativeFirstName, merchantFirstName);
      handleInputChange(setRepresentativeTitle, merchantTitle);
      handleInputChange(setRepEmail, userEmail as string);
    } else if (
      directorChecked !== initialDirectorChecked &&
      directorChecked &&
      allDirectorsEmailPresent
    ) {
      if (!directorEmails.includes(repEmail)) {
        handleInputChange(setRepresentativeLastName, "");
        handleInputChange(setRepresentativeFirstName, "");
        handleInputChange(setRepresentativeTitle, "");
        handleInputChange(setRepEmail, "");
      }
      if (directorEmails.includes(repEmail)) {
        const directorInfo = shareholdersDirectors.find(
          (user) => user.email === repEmail
        );
        if (directorInfo) {
          handleInputChange(setRepresentativeLastName, directorInfo.lastName);
          handleInputChange(setRepresentativeFirstName, directorInfo.firstName);
          handleInputChange(setRepresentativeTitle, "Director");
          handleInputChange(setRepEmail, directorInfo.email);
        }
      }
      if (!allDirectorsEmailPresent) {
        handleInputChange(setDirectorChecked, false);
      }
    }
  }, [
    authChecked,
    directorChecked,
    initialAuthChecked,
    initialDirectorChecked,
    directorEmails,
    merchantLastName,
    merchantFirstName,
    merchantTitle,
    userEmail,
    repEmail,
    representativeLastName,
    representativeFirstName,
    representativeTitle,
    shareholdersDirectors,
    allDirectorsEmailPresent,
  ]);

  /**
   * Handle number of shareholders/directors change
   */
  const onNumberOfShareholdersDirectorsChange = (value: number) => {
    handleInputChange(setNumOfShareholdersDirectors, Number(value));

    if (value < numOfShareholdersDirectors && shareholdersDirectors[value]) {
      const updatedShareholdersDirectors = [...shareholdersDirectors];
      updatedShareholdersDirectors.pop();
      handleInputChange(setShareholdersDirectors, updatedShareholdersDirectors);
    }
  };

  /**
   * Remove a specific shareholder/director by index
   */
  const removeShareholderDirector = (index: number) => {
    const directorToRemove = shareholdersDirectors[index];

    // If the removed director was selected as the representative, clear rep fields
    if (directorChecked && directorToRemove?.email === repEmail) {
      handleInputChange(setRepresentativeFirstName, "");
      handleInputChange(setRepresentativeLastName, "");
      handleInputChange(setRepresentativeTitle, "");
      handleInputChange(setRepEmail, "");
      handleInputChange(setDirectorChecked, false);
    }

    const updatedShareholdersDirectors = [...shareholdersDirectors];
    updatedShareholdersDirectors.splice(index, 1);
    handleInputChange(setShareholdersDirectors, updatedShareholdersDirectors);
    handleInputChange(
      setNumOfShareholdersDirectors,
      numOfShareholdersDirectors - 1
    );
  };

  /**
   * Handle auth director email change (when selecting from dropdown)
   */
  const handleAuthDirectorEmailChange = (email: string) => {
    const directorInfo = shareholdersDirectors.find(
      (user) => user.email === email
    );
    if (directorInfo) {
      handleInputChange(setRepresentativeLastName, directorInfo.lastName);
      handleInputChange(setRepresentativeFirstName, directorInfo.firstName);
      handleInputChange(setRepresentativeTitle, "Director");
      handleInputChange(setRepEmail, email);
    }
  };

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    // Sync to Redux before saving
    syncBorrowerRepToRedux({
      repFirstName: representativeFirstName,
      repLastName: representativeLastName,
      userFirstName: merchantFirstName,
      userLastName: merchantLastName,
      repEmail,
      repTitle: representativeTitle,
      merchantTitle,
      merchantFirstName,
      merchantLastName,
      authChecked,
      directorChecked,
      shareHoldersDirectors: shareholdersDirectors,
      numOfShareHoldersDirectors: numOfShareholdersDirectors,
    });

    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        repFirstName: representativeFirstName,
        repLastName: representativeLastName,
        userFirstName: merchantFirstName,
        userLastName: merchantLastName,
        repEmail,
        repTitle: representativeTitle,
        merchantTitle,
        authChecked,
        directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange?.(false);
          setInitialAuthChecked(authChecked);
          setInitialDirectorChecked(directorChecked);
          setInitialDraftData({
            repFirstName: representativeFirstName,
            repLastName: representativeLastName,
            userFirstName: merchantFirstName,
            userLastName: merchantLastName,
            repEmail,
            repTitle: representativeTitle,
            merchantTitle,
            authChecked,
            directorChecked,
            shareHoldersDirectors: shareholdersDirectors,
            numOfShareHoldersDirectors: numOfShareholdersDirectors,
          });

          // Trigger revalidation
          if (onSubmit) {
            onSubmit();
          }

          if (showToast) {
            toast.success("Draft Saved", { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Reset form to initial draft data
   */
  const resetForm = () => {
    if (initialDraftData) {
      const merchantFirstName = initialDraftData.userFirstName || "";
      const merchantLastName = initialDraftData.userLastName || "";
      const merchantTitle = initialDraftData.merchantTitle || "";
      const shareholdersDirectors =
        initialDraftData.shareHoldersDirectors || [];
      const numOfShareholdersDirectors = shareholdersDirectors.length || 1;

      // Determine if checkboxes were checked
      const authChecked = wasAuthCheckboxChecked({
        userFirstName: initialDraftData.userFirstName,
        userLastName: initialDraftData.userLastName,
        repEmail: initialDraftData.repEmail,
        repFirstName: initialDraftData.repFirstName,
        repLastName: initialDraftData.repLastName,
        userEmail: userEmail as string,
      });

      const directorChecked = wasDirectorCheckboxChecked({
        shareholdersDirectors,
        repEmail: initialDraftData.repEmail ?? "",
      });

      // Determine representative info based on checkboxes
      const repInfo = determineRepresentativeInfo({
        authChecked,
        directorChecked,
        merchantFirstName,
        merchantLastName,
        merchantTitle,
        userEmail: userEmail as string,
        repEmail: initialDraftData.repEmail || "",
        shareholdersDirectors,
        initialRepFirstName: initialDraftData.repFirstName || "",
        initialRepLastName: initialDraftData.repLastName || "",
        initialRepTitle: initialDraftData.repTitle || "",
      });

      // Set local states
      setRepresentativeFirstName(repInfo.repFirstName);
      setRepresentativeLastName(repInfo.repLastName);
      setRepresentativeTitle(repInfo.repTitle);
      setRepEmail(repInfo.repEmail);
      setMerchantFirstName(merchantFirstName);
      setMerchantLastName(merchantLastName);
      setMerchantTitle(merchantTitle);
      setAuthChecked(repInfo.authChecked);
      setDirectorChecked(repInfo.directorChecked);
      setInitialAuthChecked(repInfo.authChecked);
      setInitialDirectorChecked(repInfo.directorChecked);
      setShareholdersDirectors(shareholdersDirectors);
      setNumOfShareholdersDirectors(numOfShareholdersDirectors);

      // Sync to Redux
      syncBorrowerRepToRedux({
        repFirstName: repInfo.repFirstName,
        repLastName: repInfo.repLastName,
        userFirstName: merchantFirstName,
        userLastName: merchantLastName,
        repEmail: repInfo.repEmail,
        repTitle: repInfo.repTitle,
        merchantTitle,
        merchantFirstName,
        merchantLastName,
        authChecked: repInfo.authChecked,
        directorChecked: repInfo.directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      });

      setIsDirty(false);
      onDirtyChange?.(false);
    }
  };

  /**
   * Register save draft and reset form callbacks
   */
  useEffect(() => {
    if (onSaveDraft) {
      onSaveDraft(saveDraft);
    }
    if (onResetForm) {
      onResetForm(resetForm);
    }
    if (onSubmit) {
      onSubmit();
    }
  }, [onSaveDraft, onResetForm]);

  return (
    <div className={classes.container}>
      <AuthRepInfo />
      <div className={classes.inputBox}>
        <User
          authChecked={authChecked}
          setAuthChecked={(value) => handleInputChange(setAuthChecked, value)}
          error={errors}
          setMerchantFirstName={(value) => {
            handleInputChange(setMerchantFirstName, value, "merchantFirstName");
          }}
          merchantFirstName={merchantFirstName}
          setMerchantLastName={(value) => {
            handleInputChange(setMerchantLastName, value, "merchantLastName");
          }}
          merchantLastName={merchantLastName}
          setMerchantTitle={(value) => {
            handleInputChange(setMerchantTitle, value, "merchantTitle");
          }}
          merchantTitle={merchantTitle}
        />
        <img src={Line} alt="line" style={{ width: "190%" }} />
        <ShareholdersDirectors
          numOfShareholdersDirectors={numOfShareholdersDirectors}
          onNumberOfShareholdersDirectorsChange={
            onNumberOfShareholdersDirectorsChange
          }
          errors={errors}
          shareholdersDirectors={shareholdersDirectors}
          updateShareholderDirectorValue={updateShareholderDirectorValue}
          onRemoveDirector={removeShareholderDirector}
          onClearError={onClearError}
        />
        <img src={Line} alt="line" style={{ width: "190%" }} />
        {!authChecked && (
          <Auth
            numOfDirectors={numOfShareholdersDirectors}
            allDirectorsEmailPresent={allDirectorsEmailPresent}
            directorChecked={directorChecked}
            setDirectorChecked={(value) =>
              handleInputChange(setDirectorChecked, value)
            }
            error={errors}
            repEmail={repEmail}
            directorEmails={directorEmails}
            handleAuthDirectorEmailChange={handleAuthDirectorEmailChange}
            setRepEmail={(value) => {
              handleInputChange(setRepEmail, value, "repEmail");
            }}
            setRepresentativeFirstName={(value) => {
              handleInputChange(
                setRepresentativeFirstName,
                value,
                "repFirstName"
              );
            }}
            representativeFirstName={representativeFirstName}
            setRepresentativeLastName={(value) => {
              handleInputChange(
                setRepresentativeLastName,
                value,
                "repLastName"
              );
            }}
            representativeLastName={representativeLastName}
            setRepresentativeTitle={(value) => {
              handleInputChange(setRepresentativeTitle, value, "repTitle");
            }}
            representativeTitle={representativeTitle}
          />
        )}
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default BorrowerRep;
